import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { EmailRecipient } from "@cohesive/api-entities";
import { formatDistanceToNow } from "date-fns";
import { ChevronDown, ChevronUp, Paperclip, User } from "lucide-react";
import { useState } from "react";

interface EmailHeaderProps {
  senderName: string;
  senderAddress: string;
  recipients: EmailRecipient[];
  subject: string;
  timestamp: string;
  hasAttachments?: boolean;
}

export function EmailHeader({
  senderName,
  senderAddress,
  recipients,
  timestamp,
  hasAttachments,
}: EmailHeaderProps) {
  const [showAllRecipients, setShowAllRecipients] = useState(false);

  // Group recipients by type
  const toRecipients = recipients.filter((r) => r.type === "to");
  const ccRecipients = recipients.filter((r) => r.type === "cc");
  const bccRecipients = recipients.filter((r) => r.type === "bcc");

  const hasMultipleRecipients = recipients.length > 3;

  return (
    <div className="flex items-start gap-4">
      <Avatar className="h-10 w-10 shrink-0 mt-1">
        <AvatarFallback className="bg-primary/10">
          <User className="h-5 w-5 text-primary" />
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-foreground">
              {senderName || senderAddress}
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-2 mt-0.5">
              <span className="truncate">
                to{" "}
                {showAllRecipients || toRecipients.length <= 2
                  ? toRecipients.map((r) => r.name || r.address).join(", ")
                  : `${toRecipients
                      .slice(0, 2)
                      .map((r) => r.name || r.address)
                      .join(
                        ", ",
                      )}${toRecipients.length > 2 ? `, +${toRecipients.length - 2} more` : ""}`}
              </span>
              {hasAttachments && (
                <Badge variant="secondary" className="gap-1 ml-2">
                  <Paperclip className="h-3 w-3" />
                </Badge>
              )}
            </div>
          </div>
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
          </div>
        </div>

        {/* Detailed recipients (expandable) */}
        {showAllRecipients && (
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-muted-foreground w-12 shrink-0">from:</span>
              <span className="text-foreground">
                {senderName || senderAddress}
                {senderName && (
                  <span className="text-muted-foreground ml-1">
                    &lt;{senderAddress}&gt;
                  </span>
                )}
              </span>
            </div>

            {toRecipients.length > 0 && (
              <div className="flex gap-2">
                <span className="text-muted-foreground w-12 shrink-0">to:</span>
                <div className="flex-1">
                  {toRecipients.map((r, i) => (
                    <div key={r.id}>
                      {r.name || r.address}
                      {r.name && (
                        <span className="text-muted-foreground ml-1">
                          &lt;{r.address}&gt;
                        </span>
                      )}
                      {i < toRecipients.length - 1 && ","}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ccRecipients.length > 0 && (
              <div className="flex gap-2">
                <span className="text-muted-foreground w-12 shrink-0">cc:</span>
                <div className="flex-1">
                  {ccRecipients.map((r, i) => (
                    <div key={r.id}>
                      {r.name || r.address}
                      {r.name && (
                        <span className="text-muted-foreground ml-1">
                          &lt;{r.address}&gt;
                        </span>
                      )}
                      {i < ccRecipients.length - 1 && ","}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {bccRecipients.length > 0 && (
              <div className="flex gap-2">
                <span className="text-muted-foreground w-12 shrink-0">
                  bcc:
                </span>
                <div className="flex-1">
                  {bccRecipients.map((r, i) => (
                    <div key={r.id}>
                      {r.name || r.address}
                      {r.name && (
                        <span className="text-muted-foreground ml-1">
                          &lt;{r.address}&gt;
                        </span>
                      )}
                      {i < bccRecipients.length - 1 && ","}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <span className="text-muted-foreground w-12 shrink-0">date:</span>
              <span className="text-foreground">
                {new Date(timestamp).toLocaleString("en-US", {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        )}

        {hasMultipleRecipients && (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-0 text-xs text-muted-foreground hover:text-foreground mt-2"
            onClick={() => setShowAllRecipients(!showAllRecipients)}
          >
            {showAllRecipients ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                Hide details
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                Show details
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
